<template>
  <div
    class="lg:max-w-[125rem] mx-auto w-full"
    :class="{ 'grid grid-cols-2 gap-12': isGridLayout }"
  >
    <component
      :is="campaign.link?.href ? RevButtonBase : 'div'"
      v-for="(campaign, index) in campaigns"
      :key="`campaign-${index}`"
      class="w-full relative"
      :class="{
        'col-span-full': isOneUp(campaign),
        'col-span-full md:col-span-1': isTwoUp(campaign),
      }"
      :rel="campaign.link?.rel"
      :target="campaign.link?.target"
      :to="campaign.link?.href"
      @click="trackCampaignClick(campaign, index)"
    >
      <EnterViewportDetector
        class="overflow-hidden h-full"
        :class="{
          'rounded-lg': !isHero(campaign),
          'shadow-short focus-within:shadow-middle hover:shadow-middle':
            !isHero(campaign) && campaign.link?.href,
        }"
        @enter="trackCampaignImpression(campaign, index)"
      >
        <Background
          :background="campaign.background"
          :containerClass="`size-full ${!isHero(campaign) ? '!justify-end' : ''}`"
          custom-background-image-class="object-cover object-center h-full"
          :display-overlay="false"
          :fetchpriority="fetchPriority(campaign, index)"
        >
          <CampaignGradient
            v-if="displayGradient(campaign) && !isTwoUp(campaign)"
            :campaign
            :class="[!isTwoUp(campaign) && 'hidden md:block']"
          />

          <div :class="contentGridClasses(campaign)">
            <div
              v-if="campaign.image"
              class="relative min-h-[104px] overflow-hidden w-full"
              :class="{
                'md:w-[calc(100%+44px)]':
                  isOneUp(campaign) && !campaign.imagePadded,
                'self-center': isOneUp(campaign) && campaign.imagePadded,

                'order-1 md:order-2': !isTwoUp(campaign),
                'md:max-w-[540px]': campaign.imagePadded && isHero(campaign),
                'h-full': !isHero(campaign),
              }"
            >
              <ImageRenderer
                :alt="campaign.image?.alt"
                class="absolute h-full w-full flex"
                :class="{
                  'right-0 bottom-0': !campaign.imagePadded,
                  'justify-center': campaign.imagePadded,
                }"
                :fetchpriority="fetchPriority(campaign, index)"
                :height="campaign.image?.height"
                :height-mobile="campaign.image?.heightMobile"
                :image-class="imageClasses(campaign)"
                :src="campaign.image?.src"
                :src-mobile="campaign.image?.srcMobile"
                :width="campaign.image?.width"
                :width-mobile="campaign.image?.widthMobile"
              />
            </div>

            <div :class="contentClasses(campaign)">
              <CampaignGradient
                v-if="displayGradient(campaign)"
                :campaign
                :class="!isTwoUp(campaign) && 'md:hidden'"
              />

              <div
                v-if="campaign.logo"
                class="z-[1]"
                :class="{
                  '!max-w-[540px]': isHero(campaign),
                  '!max-w-[440px]': !isHero(campaign),
                }"
              >
                <RevIllustration
                  :alt="campaign.logo?.alt"
                  class="!max-h-[120px] md:ml-0 object-contain"
                  :class="{
                    'object-bottom md:object-left-bottom': isHero(campaign),
                    'object-left-bottom': !isHero(campaign),
                  }"
                  :height="campaign.logo.height"
                  :src="campaign.logo?.src"
                  :width="campaign.logo.width"
                />
              </div>

              <div
                v-if="campaign.title || campaign.subtitle"
                class="z-[1] flex flex-col gap-4"
              >
                <CampaignTitle
                  v-if="campaign.title"
                  :is-hero="isHero(campaign)"
                  :title="campaign.title"
                />

                <RichText
                  v-if="campaign.subtitle"
                  class="body-1-bold select-all"
                  :class="{
                    'max-w-[440px] mx-auto md:mx-0': isHero(campaign),
                    'max-w-[400px]': !isHero(campaign),
                  }"
                  :rich-text="campaign.subtitle"
                  style-variant="none"
                />
              </div>

              <div
                v-if="
                  (campaign.buttonLabel && campaign.link?.href) || campaign.info
                "
                class="flex gap-20 z-[1]"
                :class="{
                  'flex-col items-center md:items-start': isHero(campaign),
                  'flex-col-reverse': !isHero(campaign),
                }"
              >
                <RevButton
                  v-if="campaign.buttonLabel && campaign.link?.href"
                  class="w-fit"
                  full-width="never"
                  :size="isHero(campaign) ? 'medium' : 'small'"
                  :variant="campaign.buttonVariant ?? 'primary'"
                >
                  {{ campaign.buttonLabel }}
                </RevButton>

                <RichText
                  v-if="campaign.info"
                  class="caption text-static-default-low max-w-[440px] select-all"
                  :rich-text="campaign.info"
                  style-variant="none"
                />
              </div>
            </div>
          </div>
        </Background>
      </EnterViewportDetector>
    </component>
  </div>
</template>

<script setup lang="ts">
import type { CampaignApiProps } from '@backmarket/http-api/src/api-specs-content/models/campaign'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { tw } from '@backmarket/utils/string/tw'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'

import type { BlockProps } from '../../models/content-block'
import { isImageBackground } from '../../shared-components/Background/Background.utils'
import Background from '../../shared-components/Background/Background.vue'
import EnterViewportDetector from '../../shared-components/EnterViewportDetector/EnterViewportDetector.vue'
import ImageRenderer from '../../shared-components/ImageRenderer/ImageRenderer.vue'
import RichText from '../RichText/RichText.vue'

import CampaignGradient from './CampaignGradient.vue'
import CampaignTitle from './CampaignTitle.vue'

export type CampaignBlockProps = CampaignApiProps & BlockProps
type Campaign = CampaignBlockProps['campaigns'][number]

const { campaigns, tracking, blockPosition } = defineProps<CampaignBlockProps>()

const { trackBlockImpression, trackClick } = useTracking()

const isGridLayout = campaigns && !campaigns.find((c) => isHero(c))

const trackedIndexes = new Set<number>()

function isHero(campaign: Campaign) {
  return campaign.campaignVariant === 'hero'
}

function isOneUp(campaign: Campaign) {
  return campaign.campaignVariant === 'oneUp'
}

function isTwoUp(campaign: Campaign) {
  return campaign.campaignVariant === 'twoUp'
}

function alignmentType(campaign: Campaign) {
  if (isHero(campaign)) return campaign.contentAlignment

  return 'bottom-left'
}

function trackingData(campaign: Campaign, index: number) {
  const { title, link, buttonLabel } = campaign

  return removeEmptyValuesInObject({
    ...tracking,
    campaignPosition: index + 1,
    title: title ? documentToPlainTextString(title) : undefined,
    cta: link && buttonLabel ? buttonLabel : undefined,
  })
}

function trackCampaignImpression(campaign: Campaign, index: number) {
  if (trackedIndexes.has(index)) return

  trackedIndexes.add(index)

  trackBlockImpression(trackingData(campaign, index))
}

function trackCampaignClick(campaign: Campaign, index: number) {
  if (!campaign.link?.href) return

  trackClick(trackingData(campaign, index))
}

function displayGradient(campaign: Campaign) {
  if (!campaign.background) return false
  if (!isImageBackground(campaign.background)) return false

  return campaign.displayGradient
}

function fetchPriority(campaign: Campaign, index: number) {
  return blockPosition === 1 && index === 0 && isHero(campaign)
    ? 'high'
    : 'auto'
}

function contentGridClasses(campaign: Campaign) {
  return [
    tw`z-[1]`,

    // Hero
    isHero(campaign) &&
      tw`min-h-[584px] md:min-h-[480px] grid grid-cols-1 md:grid-cols-2 gap-y-20 md:gap-y-0 md:gap-x-40 w-full`,
    isHero(campaign) &&
      campaign.image &&
      tw`grid-rows-[minmax(104px,1fr)_auto]`,
    isHero(campaign) && campaign.imagePadded && tw`p-24 lg:py-48 lg:px-0`,

    // OneUp
    isOneUp(campaign) &&
      tw`gap-20 md:gap-40 min-h-[480px] justify-end flex flex-col w-full md:flex-row md:justify-start`,
    isOneUp(campaign) &&
      !campaign.imagePadded &&
      tw`pt-0 md:pl-24 md:pr-0 md:pb-0`,
    isOneUp(campaign) && campaign.imagePadded && tw`p-16 md:p-24`,

    // TwoUp
    isTwoUp(campaign) &&
      tw`gap-20 min-h-[480px] justify-end flex flex-col w-full`,
    isTwoUp(campaign) && campaign.imagePadded && tw`p-16 md:p-24`,
  ]
}

function contentClasses(campaign: Campaign) {
  return [
    tw`flex flex-col gap-20 relative w-full break-words`,

    // Hero
    isHero(campaign) &&
      tw`items-center text-center md:items-start md:text-left md:justify-self-end mx-auto md:mx-0 w-full md:max-w-[540px]`,
    isHero(campaign) &&
      !campaign.imagePadded &&
      tw`px-24 pb-24 md:px-0 md:py-24 md:pl-24 lg:pl-0 lg:py-48`,

    // OneUp
    isOneUp(campaign) &&
      !campaign.imagePadded &&
      tw`px-16 md:px-0 md:max-w-[440px] pb-16 md:pb-24`,
    isOneUp(campaign) && campaign.imagePadded && tw`md:max-w-[440px]`,

    // TwoUp
    isTwoUp(campaign) &&
      !campaign.imagePadded &&
      tw`px-16 pb-16 md:px-24 md:pb-24`,

    (isHero(campaign) || isOneUp(campaign)) && tw`order-2 md:order-1`,

    alignmentType(campaign) === 'center-left' &&
      tw`justify-end md:justify-center md:h-full`,
    alignmentType(campaign) === 'bottom-left' && tw`justify-end`,

    !campaign.image && tw`h-fit mt-auto`,
  ]
}

function imageClasses(campaign: Campaign) {
  if (isTwoUp(campaign)) {
    return campaign.imagePadded
      ? tw`!object-contain !w-auto`
      : tw`h-full object-bottom`
  }

  return campaign.imagePadded
    ? tw`!object-contain !w-auto`
    : tw`h-full object-bottom md:object-left-bottom`
}
</script>
